import React, {memo} from "react";
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from "remark-gfm";
import MarkdownErrorBoundary from "./MarkdownErrorBoundary";
import bubbleLoadingDarkAnimation from "assets/lottie/bubble-loading-dark.json";
import bubbleLoadingLightAnimation from "assets/lottie/bubble-loading-light.json";
import Lottie from "lottie-react";
import 'katex/dist/katex.min.css';
import 'highlight.js/styles/github-dark.css';
import "./index.scss";

interface ChatMessageProps {
    isUser: boolean;
    theme: string;
    isErrorChatGeneration?: boolean;
    isLoading?: boolean;
    message: string;
}

const ChatBubble: React.FC<ChatMessageProps> = ({ isUser, isErrorChatGeneration, isLoading, message, theme }) => {

    function convertLaTeXToMarkdown(text: string) {
        // Convert block equations \[...\] to $$...$$
        text = text.replace(/\\\[([\s\S]*?)\\\]/g, function(match, equation) {
            return '$$' + equation + '$$';
        });
        // Convert inline equations \(...\) to $...$
        text = text.replace(/\\\(([\s\S]*?)\\\)/g, function(match, equation) {
            return '$' + equation + '$';
        });
        return text;
    }

    // use to render the chat texts
    const renderChatData = () => {
        return (
            isUser ?
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                        a: ({ node, ...props }) => (
                            <a {...props} target="_blank" rel="noopener noreferrer" />
                        )
                    }}
                >
                    {message}
                </ReactMarkdown>
                :
                <MarkdownErrorBoundary>
                    <ReactMarkdown
                        remarkPlugins={[
                            [remarkMath],
                            remarkGfm
                        ]}
                        rehypePlugins={[
                            rehypeKatex, // For math rendering
                            [rehypeHighlight, { ignoreMissing: true }] // For code blocks
                        ]}
                        components={{
                            a: ({ node, ...props }) => (
                                <a {...props} target="_blank" rel="noopener noreferrer" />
                            )
                        }}
                    >
                        {convertLaTeXToMarkdown(message)}
                    </ReactMarkdown>
                </MarkdownErrorBoundary>
        )
    }
    return (
        <div
            className={`chat-message ${isUser ? "user-message" : "ai-message"}`}
            data-testid="chat-message-bubble"
        >
            <div className="message-content">
                <div
                    dir="auto"
                    data-testid="chat-message-content"
                    className={`message-bubble ${isErrorChatGeneration ? "error-chat-generation" : ""}`}
                >
                    {
                        isLoading ?
                            <Lottie
                                className='message-loading-lottie'
                                style={{width: 45, height: 25}}
                                animationData={theme === 'dark' ? bubbleLoadingDarkAnimation : bubbleLoadingLightAnimation}
                                loop={true}
                            />
                            :
                            renderChatData()
                    }
                </div>
            </div>
        </div>
    );
};

export default memo(ChatBubble);
